import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import CopyButton from '../components/CopyButton';
import PromoConfirmModal from '../components/PromoConfirmModal';
import { getPublicFiles, uploadPublicFile, deletePublicFile } from '../api';

const isImageUrl = (url) => /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(url || '');

const AdminPublicFiles = () => {
  const { t } = useTranslation();

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // ── Загрузка файла ──
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSubmitting, setUploadSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // ── Удаление файла ──
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const tRef = useRef(t);
  tRef.current = t;

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await getPublicFiles();
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setListError(e?.message || tRef.current('admin.publicFiles.loadError'));
      setList([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadList();
  }, [loadList]);

  const handleFileChange = (e) => {
    setUploadError(null);
    setSelectedFile(e.target.files?.[0] || null);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    setUploadError(null);
    setUploadSuccess(false);

    if (!selectedFile) {
      setUploadError(t('admin.publicFiles.errorFileRequired'));
      return;
    }

    setUploadSubmitting(true);
    try {
      await uploadPublicFile(selectedFile);
      setUploadSuccess(true);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadOpen(false);
      await loadList();
      window.setTimeout(() => setUploadSuccess(false), 5000);
    } catch (err) {
      setUploadError(err?.message || t('admin.publicFiles.uploadError'));
    } finally {
      setUploadSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    setDeleteSubmitting(true);
    try {
      await deletePublicFile(deleteTarget.fileKey);
      setDeleteTarget(null);
      await loadList();
    } catch (err) {
      setDeleteError(err?.message || t('admin.publicFiles.deleteError'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // Список отдаёт GET /api/dev/public-files — массив { id, fileKey, url }.
  const files = list.filter((item) => item && typeof item.url === 'string' && item.url);

  return (
    <div className="admin-promo-codes admin-promo-list-page admin-public-files">
      <div className="admin-promo-list-shell">
        <header className="admin-promo-list-header">
          <div className="admin-promo-list-header-text">
            <h3 className="admin-promo-list-title">
              <span className="admin-promo-list-title-icon admin-banner-title-icon">
                <Icon name="upload" size={18} />
              </span>
              {t('admin.publicFiles.title')}
            </h3>
            <p className="admin-promo-list-subtitle">{t('admin.publicFiles.listSubtitle')}</p>
          </div>
          <div className="admin-promo-header-actions">
            <button
              type="button"
              className="btn-premium-action-new admin-promo-refresh-btn"
              onClick={loadList}
              disabled={listLoading}
              title={t('admin.publicFiles.refresh')}
            >
              <Icon name="refresh" size={16} />
            </button>
            <button
              type="button"
              className="btn-premium-action-new"
              onClick={() => {
                setUploadOpen((v) => !v);
                setUploadError(null);
              }}
            >
              <Icon name={uploadOpen ? 'x' : 'plus-circle'} size={16} />
              <span>{uploadOpen ? t('admin.publicFiles.cancelUpload') : t('admin.publicFiles.upload')}</span>
            </button>
          </div>
        </header>

        {uploadSuccess && (
          <div className="form-status success admin-promo-banner" role="status">
            {t('admin.publicFiles.uploadSuccess')}
          </div>
        )}

        <div className={`admin-promo-create-collapse${uploadOpen ? ' is-open' : ''}`} aria-hidden={!uploadOpen}>
          <div className="admin-promo-create-collapse-inner">
            <form className="admin-promo-create-panel" onSubmit={handleUploadSubmit}>
              <div className="admin-promo-create-panel-inner">
                <h4 className="admin-promo-create-panel-title">{t('admin.publicFiles.upload')}</h4>

                <label className="admin-promo-field">
                  <span className="admin-promo-field-label">{t('admin.publicFiles.file')}</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="premium-input-minimal"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <span className="admin-banner-hint">{t('admin.publicFiles.fileHint')}</span>
                </label>

                {uploadError && (
                  <div className="form-status error" role="alert">
                    {uploadError}
                  </div>
                )}
                <div className="admin-promo-create-actions">
                  <button
                    type="submit"
                    className="btn-premium-action-new"
                    disabled={uploadSubmitting || !selectedFile}
                  >
                    <Icon name="check" size={16} />
                    <span>
                      {uploadSubmitting ? t('admin.publicFiles.submitting') : t('admin.publicFiles.submit')}
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-promo-toolbar">
          <span className="admin-promo-results-count">
            {t('admin.publicFiles.totalCount', { count: files.length })}
          </span>
        </div>

        <div className="admin-promo-list-body">
          {listLoading && <p className="admin-promo-muted admin-promo-list-empty">{t('common.loading')}</p>}
          {listError && !listLoading && (
            <div className="form-status error" role="alert">
              {listError}
            </div>
          )}
          {!listLoading && !listError && files.length === 0 && (
            <div className="admin-promo-empty-state">
              <Icon name="upload" size={40} />
              <p>{t('admin.publicFiles.empty')}</p>
              <button type="button" className="btn-premium-action-new" onClick={() => setUploadOpen(true)}>
                <Icon name="plus-circle" size={16} />
                <span>{t('admin.publicFiles.upload')}</span>
              </button>
            </div>
          )}
          {!listLoading && files.length > 0 && (
            <div className="admin-promo-table-wrap admin-promo-table-wrap--list">
              <table className="admin-promo-table admin-promo-table--list">
                <thead>
                  <tr>
                    <th>{t('admin.publicFiles.preview')}</th>
                    <th>{t('admin.publicFiles.url')}</th>
                    <th className="admin-promo-table-col-action" aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => (
                    <tr key={file.fileKey || file.url}>
                      <td>
                        {isImageUrl(file.url) ? (
                          <img src={file.url} alt="" className="admin-public-file-thumb" />
                        ) : (
                          <Icon name="file-text" size={20} />
                        )}
                      </td>
                      <td className="admin-public-file-url">
                        <code className="admin-promo-code">{file.url}</code>
                      </td>
                      <td className="admin-promo-table-col-action">
                        <div className="admin-public-file-actions">
                          <CopyButton text={file.url} />
                          <button
                            type="button"
                            className="admin-banner-icon-btn danger"
                            onClick={() => {
                              setDeleteError(null);
                              setDeleteTarget(file);
                            }}
                            title={t('admin.publicFiles.delete')}
                            aria-label={t('admin.publicFiles.delete')}
                          >
                            <Icon name="trash-2" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <PromoConfirmModal
        open={!!deleteTarget}
        title={t('admin.publicFiles.confirm.deleteTitle')}
        subtitle={t('admin.publicFiles.confirm.deleteSubtitle')}
        items={deleteTarget ? [{ label: t('admin.publicFiles.url'), value: deleteTarget.url }] : []}
        error={deleteError}
        submitting={deleteSubmitting}
        onCancel={() => {
          if (deleteSubmitting) return;
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        onConfirm={handleConfirmDelete}
        cancelLabel={t('admin.publicFiles.confirm.cancel')}
        confirmLabel={deleteSubmitting ? t('admin.publicFiles.deleting') : t('admin.publicFiles.confirm.delete')}
        titleId="public-file-delete-confirm-title"
      />
    </div>
  );
};

export default AdminPublicFiles;
